import { FC, useState, useEffect, useMemo } from 'react';
import cssClass from 'classnames';

import { usePageContext } from '../PageContext';
import { Dropdown, Input } from '../../atoms';
import { CustomModal } from '../../molecules';
import { CountryCodeList, SelectOptions, UserMobileNumberProps } from './UserMobileNumber.types';
import { StyledSection, StyledModalDiv, StyledError } from './UserMobileNumber.styles';
import { SessionMap } from './UserMobileNumber.schema';
import smsCountryCodesGql from './queries/smsCountryCodes.graphql';
import { GI_FIELD_MAX_LENGTH } from '../MemberInformation/helper';

import { useQueryFetch, gql, transformResponse, getWindowSession, eventUtil, setSessionKey } from '../../utils';
import { constants, REGEX_CONSTANTS, GQL_OPT_NAME, COUNTRY_MOBILE_NUMBER_MIN_LENGTH } from '../../constants';
import DOMPurify from 'isomorphic-dompurify';

const { USA_SMS_CODE, USA_COUNTRY_CODE_VALUE, US_MOBILE_CODE, FR_MOBILE_CODE, FR_COUNTRY_CODE, DEFAULT } = constants;

export const UserMobileNumber: FC<UserMobileNumberProps> = props => {
  const {
    phoneNumber,
    countryCode,
    placeholder,
    description,
    termsAndCondn,
    termsAndCondnOverlay,
    optoutMessage,
    invalidPhoneError,
    className = '',
    initDefaultSMSCountry,
    initDefaultSMSCountryCode,
    handleCountryCodeChange,
    phoneNumberTooltip,
    hideTnc,
    isMobileNumberMandatory = true,
    mobileNumberRegisterOptions,
    countryCodeList,
    termsAndCondnLabel = '',
    isRequiredPhoneNumber,
    isAuthRequired,
    phoneNumberDescription,
    seeTermsCtaText,
    seeTermsOverlay,
    mobileNumber,
    mobileNumberErrorMessage,
    onBlur,
    r0Checkout = false,
    isPhoneSaveToProfile,
    ...rest
  } = props;
  const { errorsProps, setValue } = rest;
  const placeholderCountry = isRequiredPhoneNumber ? constants.USA : USA_SMS_CODE;
  let countryDescription = placeholderCountry;
  let countryCodeVal = initDefaultSMSCountryCode || USA_COUNTRY_CODE_VALUE;
  const phoneNumberId = isRequiredPhoneNumber ? 'phoneNumber' : 'mobileNumber';

  const [isModalOpen, setModal] = useState(false);
  const [countryCodes, setCountryCodes] = useState<SelectOptions[]>([]);
  const [defaultSMSCountry, setDefaultSMSCountry] = useState(US_MOBILE_CODE);
  const [countrySelected, setCountrySelected] = useState(defaultSMSCountry);
  const [showOptOutMessage, setShowOptOutMessage] = useState(false);
  const isUSMobileSaveToProfile = isPhoneSaveToProfile && countrySelected === US_MOBILE_CODE;
  //Phone number phase 2
  //Setting mobile number minimum length as 10 for US country code to maintain the consistency with reservation confirmation phone number save to profile behavior
  const [minLengthMobileNumber, setMinLengthMobileNumber] = useState(isUSMobileSaveToProfile ? 10 : 7);
  const [maxLengthMobileNumber, setMaxLengthMobileNumber] = useState(10);

  const sessionObject = getWindowSession();
  const { tripsXRequestedByHeader, locale } = transformResponse(sessionObject, SessionMap);

  const { pageState = {} } = usePageContext();
  const { language } = pageState?.modelData ?? {};

  const GET_COUNTRIES_CODE = gql`
    ${smsCountryCodesGql}
  `;
  const [getCountries, { data }] = useQueryFetch(
    GET_COUNTRIES_CODE,
    GQL_OPT_NAME.smsCountryCodes,
    tripsXRequestedByHeader
  );

  useEffect(() => {
    if (countryCodeList) {
      return;
    }
    getCountries({
      variables: {
        type: isRequiredPhoneNumber ? 'COUNTRY_DIALING_CODES' : 'SMS_COUNTRY_CODES',
      },
    });
  }, [countryCodeList]);

  useEffect(() => {
    const collator = new Intl.Collator(language);
    const list: CountryCodeList = countryCodeList || data?.smsCountryCodes?.lookups || [];
    const sortedData = isRequiredPhoneNumber
      ? [...list]?.sort((countryType1, countryType2) =>
          collator.compare(countryType1?.description, countryType2?.description)
        )
      : [...list];
    let defaultCountry = '',
      defaultCountryCode = '';
    setCountryCodes(
      sortedData.map(item => {
        if (
          !defaultCountry &&
          !defaultCountryCode &&
          (pageState?.memberInformation?.smsSelectedCountry == item.label ||
            (initDefaultSMSCountry || locale)?.toUpperCase()?.match(item.label))
        ) {
          defaultCountry = item.label;
          defaultCountryCode = item.code;
        }
        return {
          label: isRequiredPhoneNumber ? `(+${item.code}) ${item.description}` : item.description,
          value: item.code,
          code: item.code,
          locale: item.label,
        };
      })
    );

    setDefaultSMSCountry(defaultCountry || placeholderCountry);
    setCountrySelected(defaultCountryCode || USA_COUNTRY_CODE_VALUE);
    setValue?.('countryCode', defaultCountryCode || USA_COUNTRY_CODE_VALUE);
    setSessionKey(constants.COUNTRY_CODE, defaultCountry || placeholderCountry);
  }, [data, countryCodeList]);

  useEffect(() => {
    if (countrySelected) {
      setMinLengthMobileNumber(
        isUSMobileSaveToProfile
          ? 10
          : COUNTRY_MOBILE_NUMBER_MIN_LENGTH[countrySelected] || COUNTRY_MOBILE_NUMBER_MIN_LENGTH[DEFAULT]
      );
      setMaxLengthMobileNumber(
        countrySelected === US_MOBILE_CODE
          ? 10
          : isRequiredPhoneNumber
          ? countrySelected?.length === 1
            ? 13
            : countrySelected?.length === 2
            ? 12
            : 11
          : GI_FIELD_MAX_LENGTH.phoneNumber
      );
      setShowOptOutMessage(countrySelected === FR_MOBILE_CODE || countrySelected === FR_COUNTRY_CODE);
    }
  }, [countrySelected, isUSMobileSaveToProfile]);

  useEffect(() => {
    const updateMobileNumber = ({
      name,
      locale,
      mobileNumber,
    }: {
      name: string;
      locale: string;
      mobileNumber: string;
    }) => {
      if (name !== rest.name) {
        return;
      }
      setDefaultSMSCountry(locale);
      rest.setValue?.(phoneNumberId, mobileNumber);
    };

    eventUtil.on(constants.UPDATE_MOBILE_NUMBER_FROM_CN_INLINE_ENROLLMENT_FORM, updateMobileNumber);

    return () => eventUtil.remove(constants.UPDATE_MOBILE_NUMBER_FROM_CN_INLINE_ENROLLMENT_FORM, updateMobileNumber);
  }, []);

  defaultSMSCountry &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    countryCodes?.forEach((dataObj: any) => {
      if (defaultSMSCountry === dataObj.locale) {
        countryDescription = dataObj.label;
        countryCodeVal = dataObj.code;
      }
    });

  const sanitizedText = DOMPurify.sanitize(optoutMessage || '', {
    RETURN_DOM: true,
    ADD_ATTR: ['href', 'target'],
  }) as HTMLElement;

  const errorMessage = errorsProps?.[phoneNumberId]?.message || errorsProps?.['countryCode']?.message;
  const isError = (errorsProps?.[phoneNumberId] || errorsProps?.['countryCode']) && 'is-error';

  const inputErrorProps = useMemo(
    () =>
      mobileNumberErrorMessage
        ? { errorsProps: { mobileNumber: { message: mobileNumberErrorMessage, type: '' } } }
        : {},
    [mobileNumberErrorMessage]
  );

  const onInputBlur = () => {
    if (!onBlur) {
      return;
    }
    onBlur({
      countryCode: countryCodeVal,
      mobileNumber: rest?.getValues ? rest?.getValues()?.[phoneNumberId] : '',
      locale: defaultSMSCountry,
    });
  };

  const renderMobileNumber = () => {
    return (
      <div className={isRequiredPhoneNumber ? 'user-mobile-number-container' : 'mobile-number-container'}>
        <Dropdown
          {...rest}
          id="countryCode"
          className="left-side"
          label={isRequiredPhoneNumber ? '' : countryCode}
          selectOptions={countryCodes}
          name="countryCode"
          testId="countryCode"
          code={countryCodeVal}
          description={countryDescription}
          isMandatoryField={true}
          varient={isRequiredPhoneNumber ? 'secondary' : 'outlined'}
          placeholder={countryDescription}
          onChange={e => {
            setCountrySelected && setCountrySelected(e.target?.value);
            setValue?.(phoneNumberId, '');
            /*
             * Refer to the implementation of the Dropdown
             * If placeholder is given
             * The first option would be a hidden node only for UI displaying
             */
            const selectedOptionIndex = e.target.selectedIndex - (countryDescription ? 1 : 0);
            setSessionKey(constants.COUNTRY_CODE, countryCodes[selectedOptionIndex]?.locale);
            handleCountryCodeChange && handleCountryCodeChange(countryCodes[selectedOptionIndex]);
            setDefaultSMSCountry && setDefaultSMSCountry(countryCodes[selectedOptionIndex]?.locale);
          }}
        />
        <Input
          {...rest}
          {...inputErrorProps}
          onBlur={onInputBlur}
          validateReset
          className="right-side"
          id={phoneNumberId}
          name={phoneNumberId}
          testId={phoneNumberId}
          type="tel"
          label={isRequiredPhoneNumber ? '' : phoneNumber}
          isErrorIcon={false}
          varient={isRequiredPhoneNumber ? 'secondary' : 'outlined'}
          isMandatoryField={isMobileNumberMandatory}
          placeholder={isRequiredPhoneNumber ? '' : placeholder}
          maxLength={rest.mobileNumberMaxLength || maxLengthMobileNumber}
          registerOptions={
            mobileNumberRegisterOptions || {
              pattern: {
                value: RegExp(REGEX_CONSTANTS.NUMBERONLYPATTERN),
                message: invalidPhoneError,
              },
              required: isAuthRequired,
              minLength: {
                value: minLengthMobileNumber,
                message: invalidPhoneError,
              },
            }
          }
          info={phoneNumberTooltip}
          isInfoIcon={false}
          isResetIcon={!r0Checkout}
        />
      </div>
    );
  };

  return (
    <StyledSection
      data-testid="UserMobileNumber"
      data-component-name="o-book-UserMobileNumber"
      className={cssClass(className, `o-book-UserMobileNumber-${defaultSMSCountry}`, {
        'r0-mobile-container': r0Checkout,
      })}
    >
      {isRequiredPhoneNumber ? (
        <>
          <fieldset className={['outlined', isError].join(' ')}>
            <legend className="outlined-label">{mobileNumber}</legend>
            {renderMobileNumber()}
          </fieldset>
          {errorMessage && (
            <StyledError className="information" id={`errormobilenumber`}>
              {errorMessage}
            </StyledError>
          )}
        </>
      ) : (
        renderMobileNumber()
      )}
      {!hideTnc && (
        <div
          className={cssClass('tnc-container', {
            'with-larger-margin-top': phoneNumberTooltip,
            'user-phone-tnc-container': isRequiredPhoneNumber,
          })}
        >
          <div className="d-flex align-items-center flex-wrap">
            <div className="text-field">
              {isRequiredPhoneNumber ? phoneNumberDescription : description}
              <a href="#" className="ctaLink" onClick={() => setModal(true)} data-testid="sms-terms-and-condition">
                {isRequiredPhoneNumber ? seeTermsCtaText : termsAndCondn.ctaText}
              </a>
            </div>
          </div>
        </div>
      )}
      {optoutMessage && sanitizedText && showOptOutMessage ? (
        <div className="optoutmsg-container">
          <div className="d-flex align-items-center flex-wrap">
            <div className="text-field" dangerouslySetInnerHTML={{ __html: sanitizedText.innerHTML }} />
          </div>
        </div>
      ) : null}
      <CustomModal
        openModal={isModalOpen}
        heading={isRequiredPhoneNumber ? termsAndCondnLabel : termsAndCondn.ctaText}
        handleClose={() => setModal(false)}
        optionsModal={false}
        isRoomRequestHeader={true}
        ariaDescribedby="termsAndConditions"
        isTnCPopup={true}
        children={
          <StyledModalDiv
            className="t-font-m modal-description"
            data-rte-editelement
            dangerouslySetInnerHTML={{ __html: `${isRequiredPhoneNumber ? seeTermsOverlay : termsAndCondnOverlay}` }}
          />
        }
      />
    </StyledSection>
  );
};
